import { create } from 'zustand';
import type { SceneWithShots, Shot } from '../types/database';

export type ViewMode = 'focused' | 'expanded';
export type EditorTab = 'video' | 'image' | 'music';

interface EditorState {
  // View state
  viewMode: ViewMode;
  editorTab: EditorTab;
  
  // Navigation state
  currentSceneIdx: number;
  currentShotIdx: number;
  manualSceneIdx: number;
  manualShotIdx: number;
  
  // Player state
  isPlaying: boolean;
  progress: number;
  autoNarrate: boolean;
  isNarrating: boolean;
  showNarration: boolean;
  
  // Local editing state
  localNarration: string;
  localVideoUrl: string;
  videoLoadedSuccess: boolean;
  
  // AI state
  isGenerating: boolean;
  isSearching: boolean;
  
  // Drag and drop
  draggedItem: { type: 'shot' | 'scene'; id: string; sceneId?: string } | null;
  
  // Actions
  setViewMode: (mode: ViewMode) => void;
  setEditorTab: (tab: EditorTab) => void;
  
  setCurrentSceneIdx: (idx: number) => void;
  setCurrentShotIdx: (idx: number) => void;
  setManualSceneIdx: (idx: number) => void;
  setManualShotIdx: (idx: number) => void;
  
  setIsPlaying: (playing: boolean) => void;
  setProgress: (progress: number) => void;
  setAutoNarrate: (auto: boolean) => void;
  setIsNarrating: (narrating: boolean) => void;
  setShowNarration: (show: boolean) => void;
  
  setLocalNarration: (text: string) => void;
  setLocalVideoUrl: (url: string) => void;
  setVideoLoadedSuccess: (success: boolean) => void;
  
  setIsGenerating: (generating: boolean) => void;
  setIsSearching: (searching: boolean) => void;
  
  setDraggedItem: (item: { type: 'shot' | 'scene'; id: string; sceneId?: string } | null) => void;
  
  // Navigation helpers
  goToNextShot: (scenes: SceneWithShots[]) => void;
  goToPrevShot: () => void;
  goToShot: (sceneIdx: number, shotIdx: number) => void;
  
  // Reset
  resetEditor: () => void;
}

const initialState = {
  viewMode: 'focused' as ViewMode,
  editorTab: 'image' as EditorTab,
  
  currentSceneIdx: 0,
  currentShotIdx: 0,
  manualSceneIdx: 0,
  manualShotIdx: 0,
  
  isPlaying: false,
  progress: 0,
  autoNarrate: true,
  isNarrating: false,
  showNarration: true,
  
  localNarration: '',
  localVideoUrl: '',
  videoLoadedSuccess: false,
  
  isGenerating: false,
  isSearching: false,
  
  draggedItem: null,
};

export const useEditorStore = create<EditorState>((set, get) => ({
  ...initialState,
  
  setViewMode: (mode) => set({ viewMode: mode }),
  setEditorTab: (tab) => set({ editorTab: tab }),
  
  setCurrentSceneIdx: (idx) => set({ currentSceneIdx: idx }),
  setCurrentShotIdx: (idx) => set({ currentShotIdx: idx }),
  setManualSceneIdx: (idx) => set({ manualSceneIdx: idx }),
  setManualShotIdx: (idx) => set({ manualShotIdx: idx }),
  
  setIsPlaying: (playing) => set({ isPlaying: playing }),
  setProgress: (progress) => set({ progress }),
  setAutoNarrate: (auto) => set({ autoNarrate: auto }),
  setIsNarrating: (narrating) => set({ isNarrating: narrating }),
  setShowNarration: (show) => set({ showNarration: show }),
  
  setLocalNarration: (text) => set({ localNarration: text }),
  setLocalVideoUrl: (url) => set({ localVideoUrl: url }),
  setVideoLoadedSuccess: (success) => set({ videoLoadedSuccess: success }),
  
  setIsGenerating: (generating) => set({ isGenerating: generating }),
  setIsSearching: (searching) => set({ isSearching: searching }),
  
  setDraggedItem: (item) => set({ draggedItem: item }),
  
  goToNextShot: (scenes) => {
    const { currentSceneIdx, currentShotIdx } = get();
    const currentScene = scenes[currentSceneIdx];
    
    if (!currentScene) return;
    
    if (currentShotIdx < (currentScene.shots?.length || 0) - 1) {
      // Next shot in same scene
      set({ 
        currentShotIdx: currentShotIdx + 1,
        progress: 0 
      });
    } else if (currentSceneIdx < scenes.length - 1) {
      // First shot of next scene
      set({ 
        currentSceneIdx: currentSceneIdx + 1,
        currentShotIdx: 0,
        progress: 0 
      });
    } else {
      // End of timeline
      set({ isPlaying: false, progress: 100 });
    }
  },
  
  goToPrevShot: () => {
    const { currentSceneIdx, currentShotIdx, manualSceneIdx, manualShotIdx } = get();
    
    if (manualShotIdx > 0) {
      set({ manualShotIdx: manualShotIdx - 1 });
    } else if (manualSceneIdx > 0) {
      set({ 
        manualSceneIdx: manualSceneIdx - 1,
        manualShotIdx: 0 // Will be set to last shot of prev scene by component
      });
    }
  },
  
  goToShot: (sceneIdx, shotIdx) => {
    set({
      currentSceneIdx: sceneIdx,
      currentShotIdx: shotIdx,
      manualSceneIdx: sceneIdx,
      manualShotIdx: shotIdx,
      isPlaying: false,
      progress: 0,
    });
  },
  
  resetEditor: () => set(initialState),
}));
